import { ensureCurrentSessionJwt } from '../auth/fetchWithCurrentSessionJwt';
import { getSessionJwt, hasActiveStytchSession } from '../auth/helpers';
import { API_URL } from '../config/config';
import { captureException, captureMessage } from '../logger/Logger';
import { fetchErrorFromResponseBody } from './errorResponseBody';
import { MissingSessionError } from './MissingSessionError';

interface FetcherArgs {
    endpoint: string;
    body?: BodyInit;
    headers?: HeadersInit;
    contentType?: 'application/json' | null;
    method?: 'DELETE' | 'GET' | 'PATCH' | 'POST';
}

const authorizationHeaders = async (): Promise<Record<string, string>> => {
    // The short-lived JWT can lapse while the member session is still valid.
    if (getSessionJwt() == null && hasActiveStytchSession()) {
        await ensureCurrentSessionJwt();
    }

    const token = getSessionJwt();
    if (token != null && token !== '') {
        return { Authorization: `Bearer ${token}` };
    }

    throw new MissingSessionError();
};

export const fetcher = async <Data = unknown>(args: FetcherArgs) => {
    const {
        endpoint,
        body,
        headers,
        method = 'GET',
        contentType = 'application/json',
    } = args;

    const url = `${API_URL}${endpoint}`;
    const authorization = await authorizationHeaders();

    const requestData: RequestInit = {
        headers: {
            ...(contentType
                ? {
                      'Content-Type': contentType,
                  }
                : {}),
            ...authorization,
            ...headers,
        },
        method,
    };

    if (method !== 'GET') {
        requestData.body = body;
    }

    const response = await fetch(url, requestData);

    if (!response.ok) {
        // Read text first. Error pages from the ALB are HTML, and response.json()
        // throws SyntaxError before we can record the status.
        const bodyText = await response.text();
        throw fetchErrorFromResponseBody({
            bodyText,
            response,
            endpoint,
            method,
        });
    }

    try {
        return await (response.json() as Promise<Data>);
    } catch (error) {
        // It's possible we hit an error due to an empty response body (which causes a SyntaxError for .json())
        if (response.headers.get('content-length') === '0') {
            captureMessage(`Received empty response body from server ${url}`);
            return '' as Data; // this is fine
        }
        // or something more complicated, like potentially a CORS issue?
        captureException(error);
        return '' as Data; // this is not fine...
    }
};
